import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import db from '../../db/db';

const Dashboard = () => {
    const navigate = useNavigate();

    const [cargando, setCargando]         = useState(true);
    const [sucursal, setSucursal]         = useState('');
    const [eventoActivo, setEventoActivo] = useState(null);
    const [empleado, setEmpleado]         = useState(null);
    const [totalVentas, setTotalVentas]   = useState(0);
    const [totalVueltas, setTotalVueltas] = useState(0);
    const [totalGastos, setTotalGastos]   = useState(0);
    const [topProductos, setTopProductos] = useState([]);
    const [pendientes, setPendientes]     = useState({
        asistencia: false,
        revision:   false,
        corte:      false,
    });

    useEffect(() => {
        const init = async () => {
            // Sesión
            const sesion   = await db.sesion.toArray();
            const logueado = sesion[0];
            setEmpleado(logueado);

            // Nombre de sucursal
            const sucursalId = logueado.sucursal?._id || logueado.sucursal;
            const sucursalReg = await db.sucursales
                .filter(s => String(s._id || s.id) === String(sucursalId))
                .first();
            setSucursal(sucursalReg?.nombre || '—');
            setEventoActivo(sucursalReg?.evento || null);

            // Conteos filtrados por sucursal
            const bySuc = x => String(x.sucursal?._id || x.sucursal) === String(sucursalId);
            const [ventas, vueltas, gastos] = await Promise.all([
                db.ventas.filter(v => bySuc(v) && v.status !== false).toArray(),
                db.vueltas.filter(v => bySuc(v) && !v.cancelada).toArray(),
                db.gastos.filter(bySuc).toArray(),
            ]);
            setTotalVentas(ventas.length);
            setTotalVueltas(vueltas.length);
            setTotalGastos(gastos.length);

            // Top 3 productos (solo ventas de esta sucursal)
            const ventaIds = new Set(ventas.map(v => v.id));
            const todosVP = await db.ventas_productos.toArray();
            const ventasProductos = todosVP.filter(vp => ventaIds.has(vp.venta_local_id));
            const conteoProductos = {};
            ventasProductos.forEach(vp => {
                const key = vp.producto;
                conteoProductos[key] = (conteoProductos[key] || 0) + (vp.cantidad || 1);
            });
            const todosProductos = await db.productos.toArray();
            const top = Object.entries(conteoProductos)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([id, cantidad]) => {
                    const prod = todosProductos.find(
                        p => String(p._id || p.id) === String(id)
                    );
                    return { nombre: prod?.nombre || id, cantidad };
                });
            setTopProductos(top);

            // Pendientes — basados en el corte activo
            const cortesLocales   = await db.cortes.filter(bySuc).toArray();
            const corteActivo     = cortesLocales.find(c => c.status === false);
            const corteFinalizado = cortesLocales.find(c => c.status === true);
            const corteEstado     = corteActivo ? 'activo' : corteFinalizado ? 'finalizado' : 'ninguno';

            let asistenciaHecha = false;
            let revisionHecha   = false;

            const corteRef = corteActivo || corteFinalizado;
            if (corteRef) {
                const [asistencia, revision] = await Promise.all([
                    db.asistencias.filter(a => a.corte_local_id === corteRef.id && bySuc(a)).first(),
                    db.revisiones.filter(r => r.corte_local_id === corteRef.id && bySuc(r)).first(),
                ]);
                asistenciaHecha = !!asistencia;
                revisionHecha   = !!revision;
            }

            setPendientes({
                asistencia: asistenciaHecha,
                revision:   revisionHecha,
                corte:      corteEstado,
            });

            setCargando(false);
        };

        init();
    }, []);

    if (cargando) {
        return (
            <div style={styles.loadingWrap}>
                <span className="spinner-border" style={{ color: '#F5C800' }} role="status" />
            </div>
        );
    }

    const hora = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    const fechaLarga = new Date().toLocaleDateString('es-MX', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });

    return (
        <div style={styles.page}>

            {/* Saludo */}
            <div style={styles.saludoRow}>
                <div>
                    <h3 style={styles.saludoTitulo}>
                        Hola, {empleado?.nombre || empleado?.usuario}
                    </h3>
                    <span style={styles.saludoSub}>
                        {fechaLarga.charAt(0).toUpperCase() + fechaLarga.slice(1)} · {hora}
                    </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <div style={styles.sucursalBadge}>
                        <i className="fas fa-store me-2" style={{ color: '#F5C800' }}></i>
                        {sucursal}
                    </div>
                    {eventoActivo?.nombre && (
                        <div style={styles.eventoBadge}>
                            <i className="fas fa-calendar me-2" style={{ color: '#A78BFA' }}></i>
                            {eventoActivo.nombre}
                        </div>
                    )}
                </div>
            </div>

            {/* Métricas principales */}
            <div style={styles.metricasGrid}>
                <MetricaCard
                    icon="fa-receipt"
                    label="Ventas"
                    valor={totalVentas}
                    color="#48BB78"
                    onClick={() => navigate('/ventas')}
                />
                <MetricaCard
                    icon="fa-rotate-left"
                    label="Ventas ambulantes"
                    valor={totalVueltas}
                    color="#63B3ED"
                    onClick={() => navigate('/vueltas')}
                />
                <MetricaCard
                    icon="fa-file-invoice-dollar"
                    label="Gastos"
                    valor={totalGastos}
                    color="#ED8936"
                    onClick={() => navigate('/gastos')}
                />
            </div>

            <div style={styles.bottomGrid}>

                {/* Top productos */}
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <i className="fas fa-star me-2" style={{ color: '#F5C800' }}></i>
                        Top 3 productos más vendidos
                    </div>
                    {topProductos.length === 0 ? (
                        <div style={styles.vacio}>
                            <i className="fas fa-box-open" style={{ fontSize: '1.8rem', color: '#333', marginBottom: '0.5rem' }}></i>
                            <p style={{ color: '#555', margin: 0, fontSize: '0.9rem' }}>Sin ventas registradas aún</p>
                        </div>
                    ) : (
                        topProductos.map((prod, idx) => (
                            <div key={prod.nombre} style={{
                                ...styles.prodFila,
                                borderBottom: idx < topProductos.length - 1 ? '1px solid #222' : 'none',
                            }}>
                                <div style={styles.prodLeft}>
                                    <span style={{ ...styles.prodRank, color: idx === 0 ? '#F5C800' : idx === 1 ? '#C0C0C0' : '#CD7F32' }}>
                                        #{idx + 1}
                                    </span>
                                    <span style={styles.prodNombre}>{prod.nombre}</span>
                                </div>
                                <span style={styles.prodCantidad}>{prod.cantidad} uds.</span>
                            </div>
                        ))
                    )}
                </div>

                {/* Pendientes */}
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <i className="fas fa-bell me-2" style={{ color: '#F5C800' }}></i>
                        Pendientes del día
                    </div>
                    <PendienteItem
                        icon="fa-clipboard-check"
                        label="Pase de lista"
                        hecho={pendientes.asistencia}
                        onClick={() => navigate('/asistencias')}
                    />
                    
                    <CorteItem
                        estado={pendientes.corte}
                        onClick={() => navigate('/corte')}
                    />

                    <PendienteItem
                        icon="fa-tasks"
                        label="Revisión"
                        hecho={pendientes.revision}
                        onClick={() => navigate('/revision')}
                    />
                </div>

            </div>
        </div>
    );
};

/* ---------- Sub-componentes ---------- */

const CORTE_CFG = {
    ninguno:    { iconColor: '#888',    iconBg: 'rgba(229,62,62,0.08)',  iconBorder: 'rgba(229,62,62,0.2)',  rowBg: 'transparent',         labelColor: '#888' },
    activo:     { iconColor: '#F5C800', iconBg: 'rgba(245,200,0,0.1)',   iconBorder: 'rgba(245,200,0,0.3)',  rowBg: 'rgba(245,200,0,0.02)', labelColor: '#C0C0C0' },
    finalizado: { iconColor: '#48BB78', iconBg: 'rgba(72,187,120,0.12)', iconBorder: 'rgba(72,187,120,0.3)', rowBg: 'rgba(72,187,120,0.04)', labelColor: '#C0C0C0' },
};

const CorteItem = ({ estado = 'ninguno', onClick }) => {
    const cfg = CORTE_CFG[estado] || CORTE_CFG.ninguno;
    return (
        <div style={{ ...styles.pendFila, cursor: 'pointer', background: cfg.rowBg }} onClick={onClick}>
            <div style={styles.pendLeft}>
                <div style={{ ...styles.pendIconWrap, background: cfg.iconBg, border: `1px solid ${cfg.iconBorder}` }}>
                    <i className="fas fa-cash-register" style={{ color: cfg.iconColor, fontSize: '0.95rem' }}></i>
                </div>
                <span style={{ ...styles.pendLabel, color: cfg.labelColor }}>Corte de caja</span>
            </div>
            <div style={styles.pendRight}>
                {estado === 'ninguno' && (
                    <span style={styles.badgePend}>
                        Sin iniciar
                        <i className="fas fa-chevron-right ms-1" style={{ fontSize: '0.7rem' }}></i>
                    </span>
                )}
                {estado === 'activo' && (
                    <span style={styles.badgeActivo}>
                        <i className="fas fa-circle me-1" style={{ fontSize: '0.5rem', verticalAlign: 'middle' }}></i>
                        Activo
                    </span>
                )}
                {estado === 'finalizado' && (
                    <span style={styles.badgeOk}>
                        <i className="fas fa-check me-1"></i>
                        Finalizado
                    </span>
                )}
            </div>
        </div>
    );
};

const MetricaCard = ({ icon, label, valor, color, onClick }) => (
    <div style={{ ...styles.metricaCard, cursor: 'pointer' }} onClick={onClick}>
        <div style={{ ...styles.metricaIconWrap, background: `${color}18`, border: `1px solid ${color}33` }}>
            <i className={`fas ${icon}`} style={{ color, fontSize: '1.3rem' }}></i>
        </div>
        <div style={styles.metricaValor}>{valor}</div>
        <div style={styles.metricaLabel}>{label}</div>
    </div>
);

const PendienteItem = ({ icon, label, hecho, onClick, okLabel = 'Completado' }) => {
    const ok = hecho;
    return (
        <div
            style={{
                ...styles.pendFila,
                cursor: 'pointer',
                background: ok ? 'rgba(72,187,120,0.04)' : 'transparent',
            }}
            onClick={onClick}
        >
            <div style={styles.pendLeft}>
                <div style={{
                    ...styles.pendIconWrap,
                    background: ok ? 'rgba(72,187,120,0.12)' : 'rgba(229,62,62,0.08)',
                    border: ok ? '1px solid rgba(72,187,120,0.3)' : '1px solid rgba(229,62,62,0.2)',
                }}>
                    <i className={`fas ${icon}`} style={{ color: ok ? '#48BB78' : '#888', fontSize: '0.95rem' }}></i>
                </div>
                <span style={{ ...styles.pendLabel, color: ok ? '#C0C0C0' : '#888' }}>{label}</span>
            </div>
            <div style={styles.pendRight}>
                {ok ? (
                    <span style={styles.badgeOk}>
                        <i className="fas fa-check me-1"></i>
                        {okLabel}
                    </span>
                ) : (
                    <span style={styles.badgePend}>
                        Pendiente
                        <i className="fas fa-chevron-right ms-1" style={{ fontSize: '0.7rem' }}></i>
                    </span>
                )}
            </div>
        </div>
    );
};

/* ---------- Estilos ---------- */

const styles = {
    page: {
        maxWidth: '1100px',
        margin: '0 auto',
    },
    loadingWrap: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 120px)',
    },
    saludoRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '0.75rem',
        marginBottom: '1.75rem',
        paddingBottom: '1.25rem',
        borderBottom: '1px solid #2E2E2E',
    },
    saludoTitulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '1.3rem',
        margin: '0 0 0.2rem',
    },
    saludoSub: {
        color: '#666',
        fontSize: '0.88rem',
    },
    sucursalBadge: {
        background: 'rgba(245,200,0,0.08)',
        border: '1px solid rgba(245,200,0,0.25)',
        color: '#F5C800',
        borderRadius: '20px',
        padding: '0.4rem 1rem',
        fontWeight: 600,
        fontSize: '0.9rem',
    },
    eventoBadge: {
        background: 'rgba(167,139,250,0.08)',
        border: '1px solid rgba(167,139,250,0.25)',
        color: '#A78BFA',
        borderRadius: '20px',
        padding: '0.4rem 1rem',
        fontWeight: 600,
        fontSize: '0.9rem',
    },
    metricasGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '1rem',
        marginBottom: '1.25rem',
    },
    metricaCard: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        padding: '1.5rem 1.25rem',
        textAlign: 'center',
        transition: 'border-color 0.2s',
    },
    metricaIconWrap: {
        width: '52px',
        height: '52px',
        borderRadius: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 1rem',
    },
    metricaValor: {
        color: '#F0F0F0',
        fontWeight: 800,
        fontSize: '2rem',
        lineHeight: 1,
        marginBottom: '0.4rem',
    },
    metricaLabel: {
        color: '#666',
        fontSize: '0.82rem',
        fontWeight: 500,
    },
    bottomGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '1rem',
    },
    card: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        overflow: 'hidden',
    },
    cardHeader: {
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '0.95rem',
        padding: '1.1rem 1.25rem',
        borderBottom: '1px solid #2E2E2E',
    },
    vacio: {
        padding: '2.5rem',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    prodFila: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.9rem 1.25rem',
    },
    prodLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    },
    prodRank: {
        fontWeight: 800,
        fontSize: '1rem',
        minWidth: '28px',
    },
    prodNombre: {
        color: '#C0C0C0',
        fontWeight: 600,
        fontSize: '0.95rem',
    },
    prodCantidad: {
        color: '#555',
        fontSize: '0.85rem',
        fontWeight: 600,
    },
    pendFila: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.9rem 1.25rem',
        borderBottom: '1px solid #1F1F1F',
        transition: 'background 0.15s',
    },
    pendLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    },
    pendIconWrap: {
        width: '36px',
        height: '36px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    pendLabel: {
        fontWeight: 600,
        fontSize: '0.95rem',
    },
    pendRight: {
        flexShrink: 0,
    },
    badgeOk: {
        background: 'rgba(72,187,120,0.1)',
        color: '#48BB78',
        border: '1px solid rgba(72,187,120,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.65rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    badgePend: {
        background: 'rgba(229,62,62,0.08)',
        color: '#E53E3E',
        border: '1px solid rgba(229,62,62,0.2)',
        borderRadius: '20px',
        padding: '0.2rem 0.65rem',
        fontSize: '0.78rem',
        fontWeight: 700,
        cursor: 'pointer',
    },
    badgeActivo: {
        background: 'rgba(245,200,0,0.1)',
        color: '#F5C800',
        border: '1px solid rgba(245,200,0,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.65rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
};

export default Dashboard;
